import { NestApiType, nestCall } from '@/composables/nestApi';
import { ContactSourceProfile } from '@/types/data-explorer-types';

/**
 * Fiches client d'un contact, une par point de vente (source de données) :
 * le site, une caisse, un import… Lecture seule, pour la fiche contact.
 */

/** État affiché d'une fiche. */
export type ContactProfileState = 'active' | 'inactive' | 'deleted';

/** Fiche telle que la renvoie GET /contacts/:id/customers. */
interface ApiContactSourceProfile {
  id_shop_customer: number;
  id_customer_shop: string | null;
  type: number;
  active: boolean;
  deleted: boolean;
  date_creation: string | null;
  date: string | null;
  source: {
    id_data_source: number;
    label: string;
    type: string;
    is_default: boolean;
    active: boolean;
    parent: { id_data_source: number; label: string } | null;
  } | null;
  source_resolution: ContactSourceProfile['sourceResolution'];
}

const SOURCE_RESOLUTIONS: ContactSourceProfile['sourceResolution'][] = ['import', 'link', 'sibling', 'default', 'none'];

export const mapContactSourceProfiles = (items: unknown): ContactSourceProfile[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item: ApiContactSourceProfile) => ({
    idShopCustomer: Number(item.id_shop_customer),
    idCustomerShop: item.id_customer_shop ?? null,
    type: Number(item.type),
    active: item.active === true,
    deleted: item.deleted === true,
    dateCreation: item.date_creation ?? null,
    date: item.date ?? null,
    source: item.source
      ? {
        idDataSource: Number(item.source.id_data_source),
        label: item.source.label ?? '',
        type: item.source.type ?? '',
        isDefault: item.source.is_default === true,
        active: item.source.active !== false,
        parent: item.source.parent
          ? {
            idDataSource: Number(item.source.parent.id_data_source),
            label: item.source.parent.label ?? '',
          }
          : null,
      }
      : null,
    sourceResolution: SOURCE_RESOLUTIONS.includes(item.source_resolution) ? item.source_resolution : 'link',
  }));
};

/**
 * Fiches du contact, ou `null` si l'appel échoue (la section affiche alors une
 * erreur plutôt qu'une liste vide trompeuse).
 */
export const getContactSourceProfilesNest = async (idShopContact: number): Promise<ContactSourceProfile[] | null> => {
  const result = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/contacts/${idShopContact}/customers`,
    {},
    '',
    undefined,
    true,
  );

  if (!result || result.statusCode !== 200 || !Array.isArray(result.data)) {
    return null;
  }

  return mapContactSourceProfiles(result.data);
};

/** Supprimée dans ShopiMind, sinon désactivée dans son point de vente, sinon active. */
export const getContactProfileState = (profile: ContactSourceProfile): ContactProfileState => {
  if (profile.deleted) {
    return 'deleted';
  }

  return profile.active ? 'active' : 'inactive';
};

/** « Parent > Magasin » pour un point de vente enfant, son nom seul sinon. */
export const getContactProfileSourceLabel = (profile: ContactSourceProfile): string => {
  if (!profile.source) {
    return '';
  }

  return profile.source.parent
    ? `${profile.source.parent.label} > ${profile.source.label}`
    : profile.source.label;
};
